import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Dialog } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { HardHat, Play, CalendarDays, Pause, Check, Plus, Search, List, LayoutGrid, MapPin, SlidersHorizontal, Users, BarChart3, Layers, Activity, Archive } from "lucide-react";
import { RegisterFeedback } from "../_shared";
import Jobs, { JobForm } from "../Jobs";
import { MANAGE_ROLES, FINANCE_ROLES } from "./jobsShared";
import { JobRow, JobCardItem, useJobsData } from "./JobRows";
import { JobsMap } from "./JobsMap";
import "./jobsRedesign.css";

const METRICS = [
  { key: "all", label: "All Jobs", icon: HardHat },
  { key: "in_progress", label: "Live", icon: Play },
  { key: "planned", label: "Upcoming", icon: CalendarDays },
  { key: "on_hold", label: "On Hold", icon: Pause },
  { key: "completed", label: "Completed", icon: Check },
];
const STATUS_ORDER = { in_progress: 0, planned: 1, on_hold: 2, completed: 3 };
const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b));

export default function JobsRedesignPreview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const manage = MANAGE_ROLES.includes(user?.role);
  const finance = FINANCE_ROLES.includes(user?.role);
  const data = useJobsData(finance, manage);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [client, setClient] = useState("");
  const [site, setSite] = useState("");
  const [crew, setCrew] = useState("");
  const [sort, setSort] = useState("status");
  const [view, setView] = useState(() => localStorage.getItem("fl_jobs_view_v2") || "list");
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get("edit");
    if (!id || !data.loaded) return;
    const job = data.jobs.find((j) => j.id === id);
    if (job && manage) setEditing(job);
    params.delete("edit");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.loaded]);
  const setViewKeep = (v) => { setView(v); localStorage.setItem("fl_jobs_view_v2", v); };

  const counts = useMemo(() => data.jobs.reduce((a, j) => ({ ...a, [j.status]: (a[j.status] || 0) + 1 }), {}), [data.jobs]);
  const ql = q.trim().toLowerCase();
  const filtered = data.jobs
    .filter((j) => status === "all" || j.status === status)
    .filter((j) => !client || j.client_name === client)
    .filter((j) => !site || j.site_address === site)
    .filter((j) => !crew || (j.assigned_user_ids || []).includes(crew))
    .filter((j) => !ql || [j.title, j.client_name, j.site_address, j.job_number].some((s) => (s || "").toLowerCase().includes(ql)))
    .sort((a, b) => sort === "title" ? (a.title || "").localeCompare(b.title || "") : sort === "start" ? (a.start_date || "9").localeCompare(b.start_date || "9")
      : sort === "oldest" ? (a.created_at || "").localeCompare(b.created_at || "")
      : sort === "status" ? ((STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9)) || (b.created_at || "").localeCompare(a.created_at || "")
      : (b.created_at || "").localeCompare(a.created_at || ""));
  const crewUsers = data.users.filter((u) => data.jobs.some((j) => (j.assigned_user_ids || []).includes(u.id))).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const ctx = { navigate, manage, edit: setEditing, reload: data.reload };
  const extraFilters = sort !== "status";

  if (!(manage || finance)) return <Jobs />;

  return (
    <div className="jr" data-testid="jobs-redesign-page">
      <section className="jr-hero">
        <div className="jr-heading"><h1>Jobs</h1><p>Manage active work, track progress and keep projects moving.</p></div>
        <div className="jr-metrics">
          {METRICS.map((m) => (
            <button key={m.key} type="button" className={`jr-metric${status === m.key ? " chosen" : ""}`} onClick={() => setStatus(m.key)} aria-pressed={status === m.key} data-testid={`jr-metric-${m.key}`}>
              <i><m.icon className="w-6 h-6" /></i>
              <strong data-testid={`jr-metric-count-${m.key}`}>{m.key === "all" ? data.jobs.length : counts[m.key] || 0}</strong><span>{m.label}</span>
            </button>
          ))}
          {manage && <button type="button" className="jr-newjob" onClick={() => navigate("/app/jobs/new")} data-testid="jr-new-job"><Plus className="w-5 h-5" />New Job</button>}
        </div>
      </section>

      <section className="jr-filters" data-testid="jr-filters">
        <label className="jr-search"><Search className="w-4 h-4" /><input className="jr-ctl" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs, clients, addresses..." aria-label="Search jobs" data-testid="jr-search" /></label>
        <select className="jr-ctl jr-sel" value={client} onChange={(e) => setClient(e.target.value)} aria-label="Client" data-testid="jr-client"><option value="">All Clients</option>{uniq(data.jobs.map((j) => j.client_name)).map((c) => <option key={c}>{c}</option>)}</select>
        <select className="jr-ctl jr-sel" value={site} onChange={(e) => setSite(e.target.value)} aria-label="Site" data-testid="jr-site"><option value="">All Sites</option>{uniq(data.jobs.map((j) => j.site_address)).map((s) => <option key={s} value={s}>{s.length > 40 ? `${s.slice(0, 40)}…` : s}</option>)}</select>
        <select className="jr-ctl jr-sel" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" data-testid="jr-status"><option value="all">All Status</option>{METRICS.slice(1).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}</select>
        <select className="jr-ctl jr-sel" value={crew} onChange={(e) => setCrew(e.target.value)} aria-label="Crew" data-testid="jr-crew"><option value="">All Crews</option>{crewUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
        <div className="jr-views" role="group" aria-label="View">
          {[["list", "List", List], ["cards", "Cards", LayoutGrid], ["map", "Map", MapPin]].map(([k, label, I]) => (
            <button key={k} type="button" className={view === k ? "selected" : ""} onClick={() => setViewKeep(k)} aria-pressed={view === k} data-testid={`jr-view-${k}`}><I className="w-4 h-4" />{label}</button>
          ))}
        </div>
        <Popover>
          <PopoverTrigger asChild><button type="button" className="jr-ctl" aria-label="More filters and sorting" data-testid="jr-more-filters"><SlidersHorizontal className={`w-5 h-5 ${extraFilters ? "text-[#FF5F15]" : ""}`} /></button></PopoverTrigger>
          <PopoverContent align="end" className="w-64 space-y-3">
            <label className="block text-sm font-medium">Sort by
              <select className="mt-1 w-full h-10 rounded-md border border-input bg-background px-2 text-sm" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="jr-sort"><option value="status">Status (Live first)</option><option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="start">Start date</option><option value="title">Title A–Z</option></select>
            </label>
            {manage && <Link to="/app/jobs/archived" className="flex items-center gap-2 text-sm font-medium text-[#c7440a]" data-testid="jr-archived-link"><Archive className="w-4 h-4" />View archived jobs</Link>}
            {(client || site || crew || q || sort !== "status" || status !== "all") && <Button variant="outline" size="sm" className="w-full" onClick={() => { setClient(""); setSite(""); setCrew(""); setQ(""); setSort("status"); setStatus("all"); }} data-testid="jr-clear-filters">Clear all filters</Button>}
          </PopoverContent>
        </Popover>
      </section>

      {data.error ? <RegisterFeedback error onRetry={data.reload} label="jobs" />
        : !data.loaded ? <div className="jr-jobs" data-testid="jr-loading">{[0, 1, 2, 3].map((i) => <div key={i} className="jr-job animate-pulse" />)}</div>
        : !filtered.length ? <div className="jr-empty" data-testid="jr-empty">{data.jobs.length ? "No jobs match these filters." : "No jobs yet — create your first job."}</div>
        : view === "map" ? <JobsMap jobs={filtered} navigate={navigate} />
        : view === "cards" ? <div className="jr-jobs jr-cardview" data-testid="jr-cards-view">{filtered.map((j) => <JobCardItem key={j.id} job={j} users={data.users} spend={data.costs ? data.costs[j.id] ?? null : undefined} finance={finance} ctx={ctx} />)}</div>
        : <div data-testid="jr-list-view">
            <div className="jr-cols" aria-hidden="true"><span>Job / Client</span><span><MapPin className="w-3.5 h-3.5" />Location</span><span><Users className="w-3.5 h-3.5" />Crew</span><span><BarChart3 className="w-3.5 h-3.5" />Progress</span><span><Layers className="w-3.5 h-3.5" />Value</span><span><CalendarDays className="w-3.5 h-3.5" />Dates</span><span><Activity className="w-3.5 h-3.5" />Status</span><span /></div>
            <div className="jr-jobs">{filtered.map((j) => <JobRow key={j.id} job={j} users={data.users} spend={data.costs ? data.costs[j.id] ?? null : undefined} finance={finance} ctx={ctx} />)}</div>
          </div>}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <JobForm key={editing.id} initial={editing} clients={data.clients} users={data.users} onDone={() => { setEditing(null); data.reload(); }} />}
      </Dialog>
    </div>
  );
}
